import React, { useState, useRef, useEffect, useMemo } from "react";
import { Globe, Search, ChevronDown, Check, X } from "lucide-react";
import { ALL_COUNTRIES } from "@/data/countries";

export default function CountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedCountry = useMemo(() => {
    return ALL_COUNTRIES.find((c) => c.code.toLowerCase() === (value || "all").toLowerCase()) || ALL_COUNTRIES[0];
  }, [value]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ALL_COUNTRIES;
    return ALL_COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q)
    );
  }, [search]);

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      // Auto-focus search input when opening
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        data-testid="country-select-btn"
        className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2.5 text-xs sm:text-sm font-semibold shadow-xs transition-all hover:border-electric hover:bg-secondary/60 focus:outline-none"
      >
        <span className="text-base leading-none">{selectedCountry.flag}</span>
        <span className="max-w-[130px] sm:max-w-[160px] truncate text-foreground font-semibold">
          {selectedCountry.name}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            open ? "rotate-180 text-electric" : ""
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 sm:w-80 rounded-2xl border border-border bg-card p-2 shadow-float backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150">
          {/* Search Box at Top */}
          <div className="relative mb-2 flex items-center rounded-xl border border-border bg-secondary/50 px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="w-full bg-transparent px-2 text-xs sm:text-sm font-medium outline-none placeholder:text-muted-foreground"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Country List */}
          <div className="max-h-64 overflow-y-auto space-y-0.5 pr-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((c) => {
                const isSelected = (value || "all").toLowerCase() === c.code.toLowerCase();
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => handleSelect(c.code)}
                    data-testid={`country-opt-${c.code}`}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs sm:text-sm transition-colors ${
                      isSelected
                        ? "bg-electric text-white font-bold"
                        : "hover:bg-secondary text-foreground font-medium"
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <span className="text-base shrink-0">{c.flag}</span>
                      <span className="truncate">{c.name}</span>
                      {c.code !== "all" && (
                        <span
                          className={`text-[11px] uppercase font-mono px-1 rounded ${
                            isSelected ? "bg-white/20 text-white" : "text-muted-foreground bg-secondary/60"
                          }`}
                        >
                          {c.code}
                        </span>
                      )}
                    </div>
                    {isSelected && <Check className="h-4 w-4 shrink-0 text-white" />}
                  </button>
                );
              })
            ) : (
              <div className="p-4 text-center text-xs text-muted-foreground">
                No country found for "{search}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
